<template>
  <SettingsPagePanel v-if="isEditing">
    <SettingsPagePanelHeader :showSubtitle="true">
      <template #title>日程調整カレンダー 編集</template>
      <template #right>
        <div>
          <FlatButton
            :class="$style.button"
            variant="secondary"
            :disabled="isButtonDisabled"
            @click="handleCancel"
          >
            中止
          </FlatButton>
          <FlatButton
            :class="$style.button"
            variant="primary"
            :disabled="isButtonDisabled"
            @click="handleSubmit"
          >
            保存
          </FlatButton>
        </div>
      </template>
    </SettingsPagePanelHeader>
    <CalendarForm v-model="form" :canEdit="canEditCalendarSetting" />
    <div :class="$style.buttonsInBottom">
      <FlatButton
        :class="$style.button"
        variant="secondary"
        :disabled="isButtonDisabled"
        @click="handleCancel"
      >
        中止
      </FlatButton>
      <FlatButton
        :class="$style.button"
        variant="primary"
        :disabled="isButtonDisabled"
        @click="handleSubmit"
      >
        この内容で保存する
      </FlatButton>
    </div>
  </SettingsPagePanel>
  <div v-else>
    <SettingsPagePanel>
      <SettingsPagePanelHeader :showSubtitle="true">
        <template #title>日程調整カレンダー</template>
        <template #right v-if="canEditCalendarSetting">
          <div>
            <FlatButton
              :class="$style.button"
              variant="caution"
              :disabled="isButtonDisabled"
              @click="handleDelete"
            >
              <template #icon>
                <IconTrash />
              </template>
              削除
            </FlatButton>
            <FlatButton
              :class="$style.button"
              variant="primary"
              :disabled="isEditing"
              @click="handleEdit"
            >
              <template #icon>
                <IconPen />
              </template>
              編集
            </FlatButton>
          </div>
        </template>
      </SettingsPagePanelHeader>
      <template v-if="calendarDetailQueryResult">
        <CalendarDetail
          :calendarGUID="calendarGUID"
          :calendar="form"
          :canEdit="canEditCalendarSetting"
        />
      </template>
    </SettingsPagePanel>
    <SettingsPagePanel :class="$style.bookingsPanel">
      <CalendarBookings :calendarGUID="calendarGUID" />
    </SettingsPagePanel>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { cloneDeep, isEqual } from 'lodash-es'

import FlatButton from '@/components/atoms/FlatButton.vue'
import IconPen from '@/components/icons/IconPen.vue'
import IconTrash from '@/components/icons/IconTrash.vue'
import type {
  UpdateCalendarInput,
  DeleteCalendarInput,
} from '@/types/schema.generated.ts'
import { useAlert } from '@/libs/alertPortal'
import { useLogger } from '@/logger/logger'
import { captureException as newrelicCaptureException } from '@/newrelic'

import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'

import {
  type FormType,
  validateForm,
  mapCalendarToForm,
  mapFormToCalendarSettingInput,
} from './formSchema'
import CalendarBookings from './CalendarBookings.vue'
import CalendarDetail from './CalendarDetail.vue'
import CalendarForm from './CalendarForm.vue'
import {
  useFetchCalendarDetailQuery,
  useUpdateCalendarMutation,
  useDeleteCalendarMutation,
  type FetchCalendarDetailQuery,
  type DeleteCalendarMutation,
} from './SettingsPageCalendarDetailView.generated.ts'
import { useCalendarSettingsPageQuery } from './SettingsPageCalendarView.generated.ts'
import { defineResponseHandlers } from '@/libs/utilities/graphql.ts'
import { APIError } from '@/libs/utilities/CustomErrors.ts'

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: {
      onConfirm: () => void
      onAbort: () => void
    }
  ): void
}>()

const alert = useAlert()
const logger = useLogger()
const route = useRoute('calendarSettingDetail')
const router = useRouter()

const calendarGUID = route.params.calendarGUID

/***********************************************
ここから権限制御関連
 ************************************************/
const { result: calendarSettingsPageQueryResult } =
  useCalendarSettingsPageQuery()
const canEditCalendarSetting = computed(
  () =>
    calendarSettingsPageQueryResult.value?.uiControl
      .editOkanekoCalendarSetting ?? false
)

/***********************************************
ここから表示データの初期化関連
 ************************************************/
const {
  result: calendarDetailQueryResult,
  refetch: calendarDetailRefetch,
  error: calendarDetailQueryError,
} = useFetchCalendarDetailQuery(
  { guid: calendarGUID },
  { fetchPolicy: 'no-cache' }
)

const initialForm = ref<FormType>({
  publicTitle: '',
  privateTitle: '',
  description: '',
  status: 'ACTIVE',
  durationMinutes: 60,
  offsetMinutes: 60,
  slotGenerationDelayHours: 18,
  slotGenerationPeriodDays: 14,
  assignedStaffs: [],
  staffSelectionStrategy: 'RANDOM',
  dailyAvailabilities: [],
  questions: [],
})
const form = ref<FormType>(cloneDeep(initialForm.value))

watchEffect(() => {
  if (calendarDetailQueryError.value) {
    throw new APIError(500, 'エラーが発生しました')
  }

  if (calendarDetailQueryResult.value) {
    if (
      calendarDetailQueryResult.value.calendarDetail.__typename ===
      'CalendarDetailNotFound'
    ) {
      throw new APIError(404, 'カレンダーが見つかりません')
    }
    if (
      calendarDetailQueryResult.value.calendarDetail.__typename ===
      'CalendarDetailPermissionDenied'
    ) {
      throw new APIError(403, 'このカレンダーの閲覧は許可されていません')
    }
    if (
      calendarDetailQueryResult.value.calendarDetail.calendar.ownerEntrance
        .guid !== route.params.entranceGUID
    ) {
      throw new APIError(
        400,
        '想定されていないURLです。開発者に連絡してください'
      )
    }

    if (
      calendarDetailQueryResult.value.calendarDetail.__typename ===
      'CalendarDetailSuccess'
    ) {
      initialForm.value = mapCalendarToForm(
        calendarDetailQueryResult.value.calendarDetail.calendar as Extract<
          FetchCalendarDetailQuery['calendarDetail'],
          {
            __typename: 'CalendarDetailSuccess'
          }
        >['calendar']
      )
      form.value = cloneDeep(initialForm.value)
    }
  }
})

/***********************************************
ここから編集関連
 ************************************************/
const isEditing = ref(false)
const handleEdit = () => {
  isEditing.value = true
}

/***********************************************
ここからキャンセル関連
 ************************************************/
const handleCancel = async () => {
  isEditing.value = false
  form.value = { ...initialForm.value }
}

/***********************************************
ここから更新関連
 ************************************************/

const { mutate: updateMutate } = useUpdateCalendarMutation()
const { mutate: deleteMutate } = useDeleteCalendarMutation()

const isSubmitting = ref(false)

const handleSubmit = async () => {
  if (isEqual(initialForm.value, form.value)) {
    alert.warning('設定内容に変更ありません')
    return
  }

  isSubmitting.value = true

  const input: UpdateCalendarInput = {
    guid: calendarGUID,
    setting: mapFormToCalendarSettingInput(form.value),
  }

  try {
    logger.ltlPeople.event.updateCalendarSetting(logger.buildMeta(), {
      data: {
        guid: calendarGUID,
        status: form.value.status,
        privateTitle:
          form.value.privateTitle !== '' ? form.value.privateTitle : undefined,
        publicTitle: form.value.publicTitle,
        description: form.value.description
          ? form.value.description
          : undefined,
        slotDurationMinutes: form.value.durationMinutes,
        slotOffsetMinutes: form.value.offsetMinutes,
        slotGenerationDelayHours: form.value.slotGenerationDelayHours,
        slotGenerationPeriodDays: form.value.slotGenerationPeriodDays,
        dailyAvailabilities: form.value.dailyAvailabilities,
        assignedStaffs: form.value.assignedStaffs,
        staffSelectionStrategy: form.value.staffSelectionStrategy,
        questions: form.value.questions,
      },
    })

    const payload = await updateMutate({ input })

    if (payload?.errors || !payload?.data) {
      throw new Error('update calendar failed')
    }

    if (!parent) {
      isSubmitting.value = false
      return
    }

    alert.success('日程調整カレンダーを更新しました。')
    await calendarDetailRefetch()
    if (
      calendarDetailQueryResult.value?.calendarDetail.__typename ===
      'CalendarDetailSuccess'
    ) {
      initialForm.value = mapCalendarToForm(
        calendarDetailQueryResult.value.calendarDetail.calendar as Extract<
          FetchCalendarDetailQuery['calendarDetail'],
          {
            __typename: 'CalendarDetailSuccess'
          }
        >['calendar']
      )
    }
    form.value = { ...initialForm.value }
    isEditing.value = false
  } catch (error) {
    newrelicCaptureException(error)
    alert.error('エラーが発生しました。')
  }

  isSubmitting.value = false
  return
}

/***********************************************
ここから削除関連
 ************************************************/

const isDeleting = ref(false)

const handleDelete = async () => {
  if (!window.confirm('本当に削除しますか？')) {
    return
  }

  isDeleting.value = true

  const input: DeleteCalendarInput = {
    guid: calendarGUID,
  }

  try {
    logger.ltlPeople.event.deleteCalendarSetting(logger.buildMeta(), {
      data: {
        guid: calendarGUID,
      },
    })

    const payload = await deleteMutate({ input })

    if (payload?.errors || !payload?.data) {
      throw new Error('delete calendar failed')
    }

    defineResponseHandlers<DeleteCalendarMutation['deleteCalendar'], void>({
      CalendarInUseByScheduleAdjustmentError: () => {
        alert.error(
          'このカレンダーは日程調整デフォルトカレンダーで使用中なので削除できません'
        )
        isDeleting.value = false
        return
      },
      DeleteCalendarSuccess: async () => {
        if (!parent) {
          isDeleting.value = false
          return
        }
        // eslint-disable-next-line fp/no-mutating-methods
        await router.push({
          name: 'calendarSetting',
          params: { entranceGUID: route.params.entranceGUID },
        })
        alert.success('日程調整カレンダーを削除しました。')
      },
    })(payload.data.deleteCalendar)
  } catch (error) {
    newrelicCaptureException(error)
    alert.error('エラーが発生しました。')
    isDeleting.value = false
    return
  }
}

/***********************************************
ここからボタンの有効無効関連
 ************************************************/
const isButtonDisabled = computed(() => {
  const errors = validateForm(form.value)
  return (
    isSubmitting.value ||
    isDeleting.value ||
    (isEditing.value && errors !== undefined)
  )
})

onBeforeRouteLeave((to, from, next) => {
  if (
    isEqual(initialForm.value, form.value) ||
    !isEditing.value ||
    isSubmitting.value ||
    isDeleting.value
  ) {
    next()
    return
  }

  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      next()
    },
    onAbort: () => {},
  })
})
</script>

<style lang="scss" module>
.button {
  &:nth-child(n + 2) {
    margin-left: 16px;
  }
}

.buttonsInBottom {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.bookingsPanel {
  margin-top: 24px;
}
</style>
